(ns practitest.frontend.similar-items
  (:require
    [ajax.core :as ajax]
    [clojure.string :as str]
    [practitest.frontend.common-entities.metadata :as metadata :refer [get-entity-name]]
    [practitest.frontend.common-entities.utils :refer [entity-icon]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components2.entity-form.forms :as forms]
    [practitest.frontend.components2.issue-status :as issue-status]
    [practitest.frontend.components2.search-box :refer [format-search-found-in]]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; Generic subs/events for similar-items, parameterized by :entity-type and :form-id

(re-frame/reg-sub
  ::similar-items
  (fn [db [_ entity-type]]
    (get-in db [:similar-items entity-type])))

(re-frame/reg-sub
  ::similar-items-active?
  (fn [db [_ form-id]]
    (get-in db [:form-states2 form-id :similar-items :active?])))

(re-frame/reg-event-db
  ::similar-items-collapse
  (fn [db [_ form-id]]
    (assoc-in db [:form-states2 form-id :similar-items :active?] false)))

(re-frame/reg-event-db
  ::clear-similar-items
  (fn [db _]
    (dissoc db :similar-items)))

(re-frame/reg-event-db
  ::similar-items-collapse
  (fn [db [_ form-id]]
    (assoc-in db [:form-states2 form-id :similar-items :active?] false)))

(re-frame/reg-event-fx
  ::similar-items-expand
  (fn [{db :db} [_ entity-type form-id]]
    {:db (assoc-in db [:form-states2 form-id :similar-items :active?] true)
     :dispatch [::fetch-similar-items entity-type form-id]}))

(re-frame/reg-event-fx
  ::fetch-similar-items
  (fn [{db :db} [_ entity-type form-id]]
    (let [current-title (or (:value (forms/get-field-state db form-id :name)) "")
          current-description (or (:value (forms/get-field-state db form-id :description)) "")
          panel-active? (get-in db [:form-states2 form-id :similar-items :active?])
          entity-id (or (get-in db [:entity entity-type :current :id])
                        (get-in db [:form-states2 form-id :entity :id]))
          project-id (subs/get-current-project-id db)
          request-params (cond-> {:p_id project-id}
                           (not (str/blank? current-title))
                           (assoc :title current-title)
                           (not (str/blank? current-description))
                           (assoc :description current-description)
                           entity-id
                           (assoc :entity_id entity-id))]
      (when (and panel-active? (or (not (str/blank? current-title))
                                   (not (str/blank? current-description))))
        {:db (update db :progress conj ::fetch-similar-items)
         :http-xhrio
         {:method          :get
          :uri             (let [query-params {:title (request-params :title)
                                               :description (request-params :description)
                                               :entity_type (if (= entity-type :test) "qtest_design" (name entity-type))
                                               :entity_id entity-id
                                               :p_id project-id
                                               :format :json}]
                             (broutes/get-url "search#find_similar" {:project_id project-id} query-params))
          :timeout         8000
          :response-format (ajax/json-response-format {:keywords? true})
          :on-success      [::fetch-similar-items-success entity-type form-id]
          :on-failure      [::fetch-similar-items-failure entity-type form-id]}}))))

(re-frame/reg-event-db
  ::fetch-similar-items-success
  (fn [db [_ entity-type form-id response]]
    (assoc-in db [:similar-items entity-type] (response :items))))

(re-frame/reg-event-db
  ::fetch-similar-items-failure
  (fn [db [_ entity-type form-id error]]
    (assoc-in db [:similar-items entity-type] {:error error})))

(defn similar-item-card
  [item entity-type]
  (let [route-name (get-in metadata/entities-metadata [entity-type :urls :edit])]
    [:div.similar-item-card
     [:div.traceability-card-row.first-row
      [:span.entity-type-icon {:class entity-type}
       [entity-icon entity-type]]
      [:span.entity-type-text
       (let [display-id (:display_id item)]
         (str (get-entity-name entity-type :singular-name) " " (when (number? display-id) "#") display-id ":"))]
      [:span.name-with-link.ellipsis
       [:a {:href (broutes/get-url-view route-name {:id (:id item)})
            ;; New tab
            :target "_BLANK"
            :on-click (fn []
                        (let [event (if (= entity-type :test)
                                      "AI Similar Tests"
                                      "AI Similar Issues")]
                          (mixpanel/track event {"Action" "Click Item"})))}
        (:name item)]]

      ;; Only show status for issues since they have a status component
      (when (= entity-type :issue)
        [:span.status-holder
         [issue-status/issue-status-component (:issue_status item)]])]

     (if (:semantic item)
       [:span.search-text-found-in
        [:span.text (get-translation "similar-items.hybrid-search" "This was found by Context search (no Exact matches)")]]
       (when-let [text (format-search-found-in (set (keys (:matches item))))]
         [:span.search-text-found-in
          (str (get-translation "similar-items.search-text-found-in" "Matches were found in") ": ")
          [:span.text text]]))

     [:div.traceability-card-row.last-row
      [:span (str (get-translation "similar-items.last-modified" "Last modified") ": " (fu/formatted-date-time (:updated_at item)))]
      [:span (str (get-translation "similar-items.last-modified-by" "By") ": " (:last_updated_by item))]]]))

(defn items-main-panel
  "Similar Items Panel for form 3, without the 'close-open' functionality, all the logic is under the tab"
  [{:keys [entity-type cnt]}]
  (let [similar-items @(re-frame/subscribe [::similar-items entity-type])]
    [:<>
     [:div.similar-items-cards-container
      (if (zero? cnt)
        [:div.no-similar-items (get-translation "similar-items.no-similar-items-found" "No similar items found")]

        ;; Otherwise, show the items
        (for [item similar-items]
          ^{:key (:id item)}
          [similar-item-card item entity-type]))]]))

(defn similar-items-tab
  ([] (similar-items-tab {}))
  ([{:keys [entity-type form-id]}]
   (let [has-data? (seq @(re-frame/subscribe [::similar-items entity-type]))
         cnt (count @(re-frame/subscribe [::similar-items entity-type]))]
     {:tab-id :similar-items
      :tab-title (if has-data?
                   (format-translation (get-translation (str "similar-" (name entity-type) "s.header-with-items") (str "Similar" " " (get-entity-name entity-type :plural-name) cnt)) {:n cnt})
                   (get-translation (str "similar-" (name entity-type) "s.header-without-items") (str "Similar" (get-entity-name entity-type :plural-name))))
      :tab-view (fn [_]
                  [items-main-panel {:entity-type entity-type :form-id form-id :cnt cnt}])})))

(defn similar-issues-panel
  [entity-type form-id]
  (let [collapsed? (not @(re-frame/subscribe [::similar-items-active? form-id]))
        similar-items @(re-frame/subscribe [::similar-items entity-type])
        cnt (count similar-items)
        has-data? (seq similar-items)]
    [:div.similar-items-panel
     {:class (dom/classes-pairs-v :collapsed collapsed?)}
     [:div.close-button
      {:class    (dom/classes-pairs :collapsed collapsed?)
       :on-click (fn []
                   (if collapsed?
                     (re-frame/dispatch [::similar-items-expand entity-type form-id])
                     (re-frame/dispatch [::similar-items-collapse form-id])))}

      [tooltip/mux-tooltip
       {:position "top"}
       (get-translation "similar-issues.open-similar-issue-tooltip" "Open Similar Issues")
       (fn [props]
         [:div
          (when collapsed?
            (assoc props :id "similar-issue-tooltip"))

          [:div.arrow-close
           [:span
            [icons/mux-similar-issues]
            (if collapsed?
              [icons/mux-arrow-left]
              [:span.similar-items-title
               (if has-data?
                 (format-translation (get-translation (str "similar-" (name entity-type) "s.header-with-items") (str "Similar" " " (get-entity-name entity-type :plural-name) cnt)) {:n cnt})
                 (get-translation (str "similar-" (name entity-type) "s.header-without-items") (str "Similar" (get-entity-name entity-type :plural-name))))])]]])]
      (when-not collapsed?
        [:div.similar-items-close
         [:span [icons/mux-arrow-right]]
         [:span {:style {:margin "1%"}} (get-translation "generic.button.close" "Close")]])]
     (if collapsed?
       [:div.similar-items-header.collapsed]
       [items-main-panel {:entity-type entity-type :form-id form-id}])]))


